import LoadingButton from "@mui/lab/LoadingButton"
import React from "react"

import {
  BG_OFF_WHITE,
  BORDER_MUTED,
  BORDER_SUBTLE,
  PRIMARY_BLUE,
  PRIMARY_BLUE_HOVER,
  TEXT_MUTED,
  TEXT_PRIMARY,
} from "@/constants/colors"
import { type GetPublicReservationQuery } from "@/graphql/graphql"
import i18n from "@/i18n"

const STYLES = {
  divider: "border-0 border-t border-solid m-0",
  section: "bg-white px-5 py-5",
  modifyBtn: "text-white rounded-[10px] h-[40px] w-full text-[14px] font-medium border-0",
  modifyBtnDisabled:
    "text-white rounded-[10px] h-[40px] w-full text-[14px] font-medium border-0 cursor-not-allowed opacity-50",
  linkRow: "flex items-center justify-center gap-2 mt-3 text-[14px]",
  blueLink: "underline cursor-not-allowed bg-transparent border-0 p-0 text-[14px] opacity-50",
  pipe: "",
  venueFooter: "rounded-b-[10px] py-5 text-center",
  venueFooterName: "text-[14px] font-semibold",
  venueFooterContact: "text-[12px] mt-1",
  venueFooterContactLink: "no-underline",
  venueFooterLinks: "flex items-center justify-center gap-2 mt-2 text-[12px]",
  venueFooterLink: "underline opacity-70",
  legalFooter: "text-center py-5 text-[12px]",
} as const

type VenueInfo = NonNullable<NonNullable<GetPublicReservationQuery["public"]["reservation"]>["venue"]["info"]>

type Props = {
  venueName: string
  venueInfo: Partial<VenueInfo> | null | undefined
  canModify?: boolean
  showCancelAction?: boolean
  modifyLoading?: boolean
  cancellationDeadlineLabel?: string | null
  onModify?: () => void
  onCancel?: () => void
}

type FooterLink = { href: string; label: string; external?: boolean }

const PRIMARY_ACTION_STYLE = {
  backgroundColor: PRIMARY_BLUE,
} as const

const SECONDARY_ACTION_STYLE = {
  color: PRIMARY_BLUE,
} as const

const DIVIDER_STYLE = {
  borderColor: BORDER_SUBTLE,
} as const

const PIPE_STYLE = {
  color: BORDER_MUTED,
} as const

const VENUE_FOOTER_STYLE = {
  backgroundColor: BG_OFF_WHITE,
} as const

const VENUE_FOOTER_NAME_STYLE = {
  color: TEXT_PRIMARY,
} as const

const VENUE_FOOTER_CONTACT_STYLE = {
  color: TEXT_MUTED,
} as const

const VENUE_FOOTER_LINK_STYLE = {
  color: TEXT_PRIMARY,
} as const

const LEGAL_FOOTER_STYLE = {
  color: TEXT_MUTED,
} as const

const DEADLINE_LABEL_STYLE = {
  color: TEXT_MUTED,
} as const

const DISABLED_ACTION_PROPS = {
  disabled: true,
  type: "button" as const,
}

const buildFooterLinks = (venueInfo: Partial<VenueInfo> | null | undefined, mapsUrl: string | null): FooterLink[] => {
  return [
    venueInfo?.email
      ? {
          href: `mailto:${venueInfo.email}`,
          label: i18n.t("app.public.reservation.contact_venue"),
        }
      : null,
    mapsUrl
      ? {
          href: mapsUrl,
          label: i18n.t("app.public.reservation.get_directions"),
          external: true,
        }
      : null,
  ].filter(Boolean) as FooterLink[]
}

type ModifyButtonProps = {
  canModify?: boolean
  modifyLoading?: boolean
  onModify?: () => void
}

const ModifyButton: React.FC<ModifyButtonProps> = ({ canModify, modifyLoading, onModify }) => {
  if (canModify) {
    return (
      <LoadingButton
        variant="contained"
        onClick={onModify}
        loading={modifyLoading}
        className={STYLES.modifyBtn}
        data-testid="modify-reservation-btn"
        sx={{
          backgroundColor: PRIMARY_BLUE,
          borderRadius: "10px",
          textTransform: "none",
          fontSize: "14px",
          fontWeight: 500,
          height: "40px",
          width: "100%",
          "&:hover": { backgroundColor: PRIMARY_BLUE_HOVER },
        }}
      >
        {i18n.t("app.public.reservation.modify_reservation")}
      </LoadingButton>
    )
  }

  return (
    <button
      {...DISABLED_ACTION_PROPS}
      className={STYLES.modifyBtnDisabled}
      style={PRIMARY_ACTION_STYLE}
      data-testid="modify-reservation-btn"
    >
      {i18n.t("app.public.reservation.modify_reservation")}
    </button>
  )
}

type CancelActionProps = {
  showCancelAction?: boolean
  onCancel?: () => void
}

const CancelAction: React.FC<CancelActionProps> = ({ showCancelAction, onCancel }) => {
  if (showCancelAction && onCancel) {
    return (
      <button
        type="button"
        onClick={onCancel}
        className="underline cursor-pointer bg-transparent border-0 p-0 text-[14px]"
        style={SECONDARY_ACTION_STYLE}
        data-testid="cancel-reservation-link"
      >
        {i18n.t("app.public.reservation.cancel_reservation")}
      </button>
    )
  }

  return (
    <button
      {...DISABLED_ACTION_PROPS}
      className={STYLES.blueLink}
      style={SECONDARY_ACTION_STYLE}
      data-testid="cancel-reservation-link"
    >
      {i18n.t("app.public.reservation.cancel_reservation")}
    </button>
  )
}

type VenueFooterBlockProps = {
  footerLinks: FooterLink[]
  phoneHref: string | null
  venueInfo: Partial<VenueInfo> | null | undefined
  venueName: string
}

const VenueFooterBlock: React.FC<VenueFooterBlockProps> = ({ footerLinks, phoneHref, venueInfo, venueName }) => (
  <>
    <hr className={STYLES.divider} style={DIVIDER_STYLE} />
    <div className={STYLES.venueFooter} style={VENUE_FOOTER_STYLE}>
      <div className={STYLES.venueFooterName} style={VENUE_FOOTER_NAME_STYLE}>
        {venueName}
      </div>
      {!!(venueInfo?.phoneFormatted || venueInfo?.email) && (
        <div className={STYLES.venueFooterContact} style={VENUE_FOOTER_CONTACT_STYLE}>
          {phoneHref && venueInfo?.phoneFormatted ? (
            <>
              <a href={phoneHref} className={STYLES.venueFooterContactLink} style={VENUE_FOOTER_CONTACT_STYLE}>
                {venueInfo.phoneFormatted}
              </a>
              {!!venueInfo?.email && <span> • </span>}
            </>
          ) : null}
          {venueInfo?.email || null}
        </div>
      )}
      {footerLinks.length > 0 && (
        <div className={STYLES.venueFooterLinks}>
          {footerLinks.map((link, index) => (
            <React.Fragment key={link.label}>
              {index > 0 && (
                <span className={STYLES.pipe} style={PIPE_STYLE}>
                  |
                </span>
              )}
              <a
                href={link.href}
                className={STYLES.venueFooterLink}
                style={VENUE_FOOTER_LINK_STYLE}
                target={link.external ? "_blank" : undefined}
                rel={link.external ? "noopener noreferrer" : undefined}
              >
                {link.label}
              </a>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>

    <div className={STYLES.legalFooter} style={LEGAL_FOOTER_STYLE}>
      <div>{i18n.t("app.public.reservation.made_with_tabb")}</div>
      <div>{i18n.t("app.public.reservation.privacy_terms")}</div>
    </div>
  </>
)

const ReservationFooter: React.FC<Props> = ({
  venueName,
  venueInfo,
  canModify,
  showCancelAction,
  modifyLoading,
  cancellationDeadlineLabel,
  onModify,
  onCancel,
}) => {
  const mapsUrl =
    venueInfo?.latitude && venueInfo?.longitude
      ? `https://www.google.com/maps?q=${venueInfo.latitude},${venueInfo.longitude}`
      : null
  const phoneHref = venueInfo?.phone ? `tel:${venueInfo.phone}` : null
  const footerLinks = buildFooterLinks(venueInfo, mapsUrl)

  return (
    <>
      <hr className={STYLES.divider} style={DIVIDER_STYLE} />
      <div className={STYLES.section + " text-center"}>
        <ModifyButton canModify={canModify} modifyLoading={modifyLoading} onModify={onModify} />
        {cancellationDeadlineLabel ? (
          <div className="mt-3 text-[12px]" style={DEADLINE_LABEL_STYLE}>
            {cancellationDeadlineLabel}
          </div>
        ) : null}
        <div className={STYLES.linkRow}>
          <button
            {...DISABLED_ACTION_PROPS}
            className={STYLES.blueLink}
            style={SECONDARY_ACTION_STYLE}
            data-testid="add-to-calendar-link"
          >
            {i18n.t("app.public.reservation.add_to_calendar")}
          </button>
          <span className={STYLES.pipe} style={PIPE_STYLE}>
            |
          </span>
          <CancelAction showCancelAction={showCancelAction} onCancel={onCancel} />
        </div>
      </div>

      <VenueFooterBlock footerLinks={footerLinks} phoneHref={phoneHref} venueInfo={venueInfo} venueName={venueName} />
    </>
  )
}

export default ReservationFooter
